import React, { useState, useEffect } from "react";
import { X, Save, Eye, AlertCircle, CheckCircle, DollarSign } from "lucide-react";
import apiService from "../services/api";
import "../styles/BulkRateUpdateModal.css";

const BulkRateUpdateModal = ({ employees, onClose, onSuccess }) => {
  const [adminPassword, setAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("edit"); // "edit" or "preview"
  const [filterText, setFilterText] = useState("");

  // Initialize updates from employees
  useEffect(() => {
    const initialUpdates = employees.map((emp) => ({
      employee_id: emp.employee_id,
      employee_name: emp.employee_name,
      current_hour_rate: emp.hour_rate || "",
      current_weekend_rate: emp.weekend_hour_rate || "",
      hour_rate: emp.hour_rate || "",
      weekend_hour_rate: emp.weekend_hour_rate || "",
    }));
    setUpdates(initialUpdates);
  }, [employees]);

  const filteredUpdates = updates.filter((u) =>
    u.employee_name?.toLowerCase().includes(filterText.toLowerCase())
  );

  const hasChanges = () => {
    return updates.some(
      (u) =>
        String(u.hour_rate) !== String(u.current_hour_rate) ||
        String(u.weekend_hour_rate) !== String(u.current_weekend_rate)
    );
  };

  const changedCount = () => {
    return updates.filter(
      (u) =>
        String(u.hour_rate) !== String(u.current_hour_rate) ||
        String(u.weekend_hour_rate) !== String(u.current_weekend_rate)
    ).length;
  };

  const handleRateChange = (employeeId, field, value) => {
    setUpdates((prev) =>
      prev.map((u) =>
        u.employee_id === employeeId ? { ...u, [field]: value } : u
      )
    );
    setPreview(null);
    setError(null);
  };

  const handlePreview = async () => {
    if (!adminPassword) {
      setError("נא להזין סיסמת מנהל");
      return;
    }

    const changedUpdates = updates
      .filter(
        (u) =>
          String(u.hour_rate) !== String(u.current_hour_rate) ||
          String(u.weekend_hour_rate) !== String(u.current_weekend_rate)
      )
      .map((u) => ({
        employee_id: u.employee_id,
        hour_rate: u.hour_rate ? parseFloat(u.hour_rate) : null,
        weekend_hour_rate: u.weekend_hour_rate
          ? parseFloat(u.weekend_hour_rate)
          : null,
      }));

    if (changedUpdates.length === 0) {
      setError("אין שינויים לתצוגה מקדימה");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.previewBulkRateUpdate(
        adminPassword,
        changedUpdates
      );
      if (response.success) {
        setPreview(response.preview);
        setActiveTab("preview");
      } else {
        setError(response.error || "שגיאה בתצוגה מקדימה");
      }
    } catch (err) {
      setError(err.response?.data?.error || "שגיאה בתצוגה מקדימה");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!adminPassword) {
      setError("נא להזין סיסמת מנהל");
      return;
    }

    const changedUpdates = updates
      .filter(
        (u) =>
          String(u.hour_rate) !== String(u.current_hour_rate) ||
          String(u.weekend_hour_rate) !== String(u.current_weekend_rate)
      )
      .map((u) => ({
        employee_id: u.employee_id,
        hour_rate: u.hour_rate ? parseFloat(u.hour_rate) : null,
        weekend_hour_rate: u.weekend_hour_rate
          ? parseFloat(u.weekend_hour_rate)
          : null,
      }));

    if (changedUpdates.length === 0) {
      setError("אין שינויים לשמירה");
      return;
    }

    if (
      !window.confirm(
        `האם אתה בטוח שברצונך לעדכן ${changedUpdates.length} עובדים?`
      )
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.bulkUpdateRates(
        adminPassword,
        changedUpdates
      );
      if (response.success) {
        setSuccess(
          `עודכנו ${response.results.success.length} עובדים בהצלחה`
        );
        if (response.results.failed.length > 0) {
          setError(
            `${response.results.failed.length} עדכונים נכשלו`
          );
        }
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(response.error || "שגיאה בעדכון");
      }
    } catch (err) {
      setError(err.response?.data?.error || "שגיאה בעדכון");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (field, value) => {
    setUpdates((prev) =>
      prev.map((u) => ({
        ...u,
        [field]: value,
      }))
    );
    setPreview(null);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="bulk-rate-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            <DollarSign size={24} />
            עדכון תעריפים מרוכז
          </h2>
          <button className="btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Admin Password */}
          <div className="password-section">
            <label>סיסמת מנהל:</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="הזן סיסמת מנהל..."
              />
              <button
                type="button"
                className="btn-toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "הסתר" : "הצג"}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button
              className={activeTab === "edit" ? "active" : ""}
              onClick={() => setActiveTab("edit")}
            >
              עריכה ({changedCount()} שינויים)
            </button>
            <button
              className={activeTab === "preview" ? "active" : ""}
              onClick={() => setActiveTab("preview")}
              disabled={!preview}
            >
              תצוגה מקדימה
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={18} />
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success">
              <CheckCircle size={18} />
              {success}
            </div>
          )}

          {/* Edit Tab */}
          {activeTab === "edit" && (
            <>
              {/* Filter */}
              <div className="filter-section">
                <input
                  type="text"
                  placeholder="חפש עובד..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="filter-input"
                />
                <span className="results-count">
                  {filteredUpdates.length} עובדים
                </span>
              </div>

              {/* Bulk Actions */}
              <div className="bulk-actions">
                <label>עדכן הכל:</label>
                <input
                  type="number"
                  placeholder="תעריף רגיל"
                  onChange={(e) =>
                    handleSelectAll("hour_rate", e.target.value)
                  }
                  className="bulk-input"
                />
                <input
                  type="number"
                  placeholder="תעריף סוף שבוע"
                  onChange={(e) =>
                    handleSelectAll("weekend_hour_rate", e.target.value)
                  }
                  className="bulk-input"
                />
              </div>

              {/* Employees Table */}
              <div className="employees-table-container">
                <table className="employees-table">
                  <thead>
                    <tr>
                      <th>שם עובד</th>
                      <th>תעריף נוכחי</th>
                      <th>תעריף חדש</th>
                      <th>תעריף סופ"ש נוכחי</th>
                      <th>תעריף סופ"ש חדש</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUpdates.map((u) => {
                      const hourRateChanged =
                        String(u.hour_rate) !==
                        String(u.current_hour_rate);
                      const weekendRateChanged =
                        String(u.weekend_hour_rate) !==
                        String(u.current_weekend_rate);

                      return (
                        <tr
                          key={u.employee_id}
                          className={
                            hourRateChanged || weekendRateChanged
                              ? "changed"
                              : ""
                          }
                        >
                          <td>{u.employee_name}</td>
                          <td className="current-value">
                            {u.current_hour_rate || "-"}
                          </td>
                          <td>
                            <input
                              type="number"
                              value={u.hour_rate}
                              onChange={(e) =>
                                handleRateChange(
                                  u.employee_id,
                                  "hour_rate",
                                  e.target.value
                                )
                              }
                              className={
                                hourRateChanged ? "changed" : ""
                              }
                            />
                          </td>
                          <td className="current-value">
                            {u.current_weekend_rate || "-"}
                          </td>
                          <td>
                            <input
                              type="number"
                              value={u.weekend_hour_rate}
                              onChange={(e) =>
                                handleRateChange(
                                  u.employee_id,
                                  "weekend_hour_rate",
                                  e.target.value
                                )
                              }
                              className={
                                weekendRateChanged ? "changed" : ""
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Preview Tab */}
          {activeTab === "preview" && preview && (
            <div className="preview-section">
              <h3>תצוגה מקדימה של שינויים</h3>
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>שם עובד</th>
                    <th>סטטוס</th>
                    <th>תעריף נוכחי</th>
                    <th>תעריף מוצע</th>
                    <th>תעריף סופ"ש נוכחי</th>
                    <th>תעריף סופ"ש מוצע</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p) => (
                    <tr
                      key={p.employee_id}
                      className={p.status === "error" ? "error" : ""}
                    >
                      <td>{p.employee_name || p.employee_id}</td>
                      <td>
                        {p.status === "ok" ? (
                          <span className="status-ok">✓ תקין</span>
                        ) : (
                          <span className="status-error">
                            ✗ {p.error}
                          </span>
                        )}
                      </td>
                      <td>{p.current_hour_rate || "-"}</td>
                      <td
                        className={
                          p.proposed_hour_rate !== "no change"
                            ? "proposed"
                            : ""
                        }
                      >
                        {p.proposed_hour_rate}
                      </td>
                      <td>{p.current_weekend_rate || "-"}</td>
                      <td
                        className={
                          p.proposed_weekend_rate !== "no change"
                            ? "proposed"
                            : ""
                        }
                      >
                        {p.proposed_weekend_rate}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            ביטול
          </button>
          {activeTab === "edit" && (
            <button
              className="btn-preview"
              onClick={handlePreview}
              disabled={loading || !hasChanges()}
            >
              <Eye size={18} />
              {loading ? "טוען..." : "תצוגה מקדימה"}
            </button>
          )}
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={loading || !hasChanges()}
          >
            <Save size={18} />
            {loading ? "שומר..." : "שמור שינויים"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkRateUpdateModal;
